import CanvasEffect from "../canvas-effects/canvas-effect.js";
import { sequencerSocket, SOCKET_HANDLERS } from "../sockets.js";
import * as lib from "../lib/lib.js";
import flagManager from "../utils/flag-manager.js";
import CONSTANTS from "../constants.js";
import SequenceManager from "./sequence-manager.js";
import { EffectsUIApp } from "../formapplications/effects-ui/effects-ui-app.js";
import PasteManager from "./sequencer-paste-manager.js";

const PositionContainer = new Map();
const TemporaryPositionsContainer = new Map();

export default class SequencerEffectManager {
	/**
	 * Returns all the currently running effects on the canvas
	 *
	 * @returns {Array}
	 */
	static get effects() {
		return Array.from(SequenceManager.VisibleEffects.values());
	}

	static _updatePosition(uuid, position) {
		TemporaryPositionsContainer.set(uuid, position);
	}

	static getPositionForUUID(uuid) {
		return TemporaryPositionsContainer.get(uuid);
	}

	/**
	 * Opens the Sequencer Manager UI with the effects tab open
	 */
	static show() {
		return EffectsUIApp.show({ tab: "manager" });
	}

	/**
	 * Play an effect on the canvas.
	 *
	 * @param {object} data The data that describes the audio to play
	 * @param {boolean} [push=true] A flag indicating whether or not to make other clients play the effect
	 * @returns {CanvasEffect} A CanvasEffect object
	 */
	static async play(data, push = true) {
		if (!lib.user_can_do("permissions-effect-create")) {
			lib.custom_warning(
				"Sequencer",
				"EffectManager | play | Players do not have permissions to play effects. This can be configured in Sequencer's module settings."
			);
			return { duration: Promise.resolve(0), promise: Promise.resolve() };
		}
		if (push) sequencerSocket.executeForOthers(SOCKET_HANDLERS.PLAY_EFFECT, data);
		return this._playEffect(data);
	}

	/**
	 * Get effects that are playing on the canvas based on a set of filters
	 *
	 * @param {object} inFilter An object containing filters that determine which effects to return
	 *                             - object: An ID or a PlaceableObject
	 *                             - name: The name of the effect
	 *                             - sceneId: the ID of the scene to search within
	 * @returns {Array} An array containing effects that match the given filter
	 */
	static getEffects(inFilter = {}) {
		const filters = this._validateFilters(inFilter);
		if (!inFilter)
			throw lib.custom_error(
				"Sequencer",
				"EffectManager | getEffects | Incorrect or incomplete parameters provided"
			);
		return this._filterEffects(filters);
	}

	/**
	 * Updates effects based on a set of filters
	 *
	 * @param {object} inFilter An object containing filters that determine which effects to return
	 *                             - object: An ID or a PlaceableObject
	 *                             - name: The name of the effect
	 *                             - sceneId: the ID of the scene to search within
	 *                             - effects: a single CanvasEffect or its ID, or an array of such
	 * @param {object} inUpdates
	 * @returns {promise}
	 */
	static updateEffects(inFilter, inUpdates) {
		inFilter = this._validateFilters(inFilter);
		if (!inFilter)
			throw lib.custom_error(
				"Sequencer",
				"EffectManager | updateEffects | Incorrect or incomplete parameters provided"
			);

		CanvasEffect.validateUpdate(inUpdates);

		const effectsToUpdate = this._filterEffects(inFilter).filter(
			(effect) => effect.userCanUpdate
		);

		return Promise.allSettled(
			effectsToUpdate.map((effect) => effect.update(inUpdates))
		);
	}

	/**
	 * End effects that are playing on the canvas based on a set of filters
	 *
	 * @param {object} inFilter An object containing filters that determine which effects to end
	 *                             - object: An ID or a PlaceableObject
	 *                             - name: The name of the effect
	 *                             - sceneId: the ID of the scene to search within
	 *                             - effects: a single CanvasEffect or its ID, or an array of such
	 * @param {boolean} [push=true] A flag indicating whether or not to make other clients end the effects
	 * @returns {promise} A promise that resolves when the effects have ended
	 */
	static async endEffects(inFilter = {}, push = true) {
		inFilter = this._validateFilters(inFilter);
		if (!inFilter)
			throw lib.custom_error(
				"Sequencer",
				"EffectManager | endEffects | Incorrect or incomplete parameters provided"
			);
		const effectsToEnd = this._getEffectsByFilter(inFilter);
		if (!effectsToEnd.length) return;
		if (push)
			sequencerSocket.executeForOthers(
				SOCKET_HANDLERS.END_EFFECTS,
				effectsToEnd
			);
		return this._endManyEffects(effectsToEnd);
	}

	/**
	 * End all effects that are playing on the canvas
	 *
	 * @param {string} [inSceneId] A parameter which determines which scene to end all effects on, defaults to current viewed scene
	 * @param {boolean} [push=true] A flag indicating whether or not to make other clients end all effects
	 * @returns {promise} A promise that resolves when all of the effects have _ended
	 */
	static async endAllEffects(inSceneId = game.user.viewedScene, push = true) {
		const inFilter = this._validateFilters({ sceneId: inSceneId });
		if (!inFilter)
			throw lib.custom_error(
				"Sequencer",
				"EffectManager | endAllEffects | Incorrect or incomplete parameters provided"
			);
		const effectsToEnd = this._getEffectsByFilter(inFilter);
		if (!effectsToEnd.length) return;
		if (push)
			sequencerSocket.executeForOthers(
				SOCKET_HANDLERS.END_EFFECTS,
				effectsToEnd
			);
		return this._endManyEffects(effectsToEnd);
	}

	static _getEffectsByFilter(inFilter) {
		return lib.make_array_unique(
			this._filterEffects(inFilter)
				.filter((effect) => effect.userCanDelete)
				.map((effect) => {
					return effect.data?.persistOptions?.persistTokenPrototype && lib.is_UUID(effect?.data.source) && effect.data?.attachTo?.active
						? effect.data?.persistOptions?.id ?? effect.id
						: effect.id;
				})
		);
	}

	/**
	 * If an effect has been named its position will be cached, which can be retrieved with this method
	 *
	 * @param {string} inName
	 * @returns {object|boolean}
	 * @private
	 */
	static getEffectPositionByName(inName) {
		if (!(typeof inName === "string"))
			throw lib.custom_error(
				"Sequencer",
				"EffectManager | getEffectPositionByName | inName must be of type string"
			);
		return PositionContainer.get(inName) ?? false;
	}

	/**
	 * Filters the existing effects based on the given filter
	 *
	 * @param inFilter
	 * @returns {array}
	 * @private
	 */
	static _filterEffects(inFilter) {
		if (inFilter.name) {
			inFilter.name = new RegExp(
				"^" + lib.str_to_search_regex_str(lib.safe_str(inFilter.name)) + "$",
				"gu"
			);
		}
		return this.effects.filter((effect) => {
			return (
				(!inFilter.effects || inFilter.effects.includes(effect.id)) &&
				(!inFilter.name || (effect.data.name && effect.data.name.match(inFilter.name)?.length)) &&
				(!inFilter.source || inFilter.source === effect.data.source) &&
				(!inFilter.target || inFilter.target === effect.data.target) &&
				(!inFilter.origin || inFilter.origin === effect.data.origin)
			);
		});
	}

	/**
	 * Validates an object actually exists, and gets its UUID
	 *
	 * @param object
	 * @param sceneId
	 * @returns {string}
	 * @private
	 */
	static _validateObject(object, sceneId) {
		if (
			!(
				object instanceof foundry.abstract.Document ||
				object instanceof foundry.canvas.placeables.PlaceableObject ||
				typeof object === "string"
			)
		) {
			throw lib.custom_error(
				"Sequencer",
				"EffectManager | object must be instance of PlaceableObject or of type string"
			);
		} else if (
			object instanceof foundry.canvas.placeables.PlaceableObject ||
			object instanceof foundry.abstract.Document
		) {
			object = lib.get_object_identifier(object?.document ?? object);
		} else if (typeof object === "string") {
			const actualObject = lib.get_object_from_scene(object, sceneId);
			if (!actualObject) {
				throw lib.custom_error(
					"Sequencer",
					`EffectManager | could not find object with ID: ${object}`
				);
			}
			const uuid = lib.get_object_identifier(actualObject);
			if (!uuid) {
				throw lib.custom_error(
					"Sequencer",
					`EffectManager | could could not establish identifier of object with ID: ${object}`
				);
			}
			object = uuid;
		}

		return object;
	}

	/**
	 * Validates the filter given to any of the above public methods
	 *
	 * @param inFilter
	 * @returns {boolean}
	 * @private
	 */
	static _validateFilters(inFilter) {
		if (inFilter?.sceneId) {
			if (typeof inFilter.sceneId !== "string")
				throw lib.custom_error(
					"Sequencer",
					"EffectManager | inFilter.sceneId must be of type string"
				);
			if (!game.scenes.get(inFilter.sceneId))
				throw lib.custom_error(
					"Sequencer",
					"EffectManager | inFilter.sceneId must be a valid scene id (could not find scene)"
				);
		} else {
			inFilter.sceneId = game.user.viewedScene;
		}

		if (inFilter?.object) {
			inFilter.source = this._validateObject(inFilter.object, inFilter.sceneId);
			delete inFilter.object;
		}

		if (inFilter?.source) {
			inFilter.source = this._validateObject(inFilter.source, inFilter.sceneId);
		}

		if (inFilter?.target) {
			inFilter.target = this._validateObject(inFilter.target, inFilter.sceneId);
		}

		if (inFilter?.name && typeof inFilter?.name !== "string")
			throw lib.custom_error(
				"Sequencer",
				"EffectManager | inFilter.name must be of type string"
			);
		if (inFilter?.origin && typeof inFilter?.origin !== "string")
			throw lib.custom_error(
				"Sequencer",
				"EffectManager | inFilter.origin must be of type string"
			);

		if (inFilter?.effects) {
			if (!Array.isArray(inFilter.effects))
				inFilter.effects = [inFilter.effects];
			inFilter.effects = inFilter.effects.map((effect) => {
				if (!(typeof effect === "string" || effect instanceof CanvasEffect))
					throw lib.custom_error(
						"Sequencer",
						"EffectManager | collections in inFilter.effects must be of type string or CanvasEffect"
					);
				if (effect instanceof CanvasEffect) return effect.id;
				return effect;
			});
		}

		if (
			!inFilter.name &&
			!inFilter.origin &&
			!inFilter.target &&
			!inFilter.sceneId &&
			!inFilter.effects &&
			!inFilter.origin
		)
			return false;

		return foundry.utils.mergeObject(
			{
				effects: false,
				name: false,
				source: false,
				target: false,
				sceneId: false,
				origin: false,
			},
			inFilter
		);
	}

	/**
	 * Actually plays the effect on the canvas
	 *
	 * @param data
	 * @param setFlags
	 * @returns {Promise<{duration: Promise<number>, promise: Promise<void>}>}
	 * @private
	 */
	static async _playEffect(data, setFlags = true) {
		const effect = CanvasEffect.make(data);
		if (
			data.persist &&
			setFlags &&
			effect.context &&
			effect.owner &&
			!effect.isSourceTemporary &&
			!data.temporary &&
			!data.remote
		) {
			flagManager.addFlags(effect.context.uuid, { effects: effect.data });
		}

		if (!effect.shouldPlay) {
			return { duration: Promise.resolve(0), promise: Promise.resolve() };
		}

		const playData = effect.play();

		SequenceManager.VisibleEffects.add(effect.id, effect);
		if (effect.data.name) {
			effect._addToTicker(() => {
				if (effect.isDestroyed) return;
				PositionContainer.set(effect.data.name, {
					start: effect.sourcePosition,
					end: effect.targetPosition,
				});
			});
		}

		if (data.temporary && effect.owner) {
			let lastSource = null;
			let lastTarget = null;
			effect._addToTicker(() => {
				if (effect.source && !effect.isSourceDestroyed) {
					const sourceData = effect.getSourceData();
					const pos = sourceData.position;
					if (
						!lastSource
						|| lastSource.x !== pos?.x
						|| lastSource.y !== pos?.y
						|| lastSource.elevation !== pos?.elevation
						|| lastSource.width !== sourceData.width
						|| lastSource.height !== sourceData.height
						|| lastSource.rotation !== sourceData.rotation
						|| lastSource.alpha !== sourceData.alpha
					) {
						sequencerSocket.executeForOthers(
							SOCKET_HANDLERS.UPDATE_EFFECT_POSITION,
							data.source,
							sourceData
						);
						lastSource = {
							x: pos?.x,
							y: pos?.y,
							elevation: pos?.elevation,
							width: sourceData.width,
							height: sourceData.height,
							rotation: sourceData.rotation,
							alpha: sourceData.alpha,
						};
					}
				}
				if (effect.target && !effect.isTargetDestroyed) {
					const targetData = effect.getTargetData();
					const pos = targetData.position;
					if (
						!lastTarget
						|| lastTarget.x !== pos?.x
						|| lastTarget.y !== pos?.y
						|| lastTarget.elevation !== pos?.elevation
						|| lastTarget.width !== targetData.width
						|| lastTarget.height !== targetData.height
						|| lastTarget.rotation !== targetData.rotation
						|| lastTarget.alpha !== targetData.alpha
					) {
						sequencerSocket.executeForOthers(
							SOCKET_HANDLERS.UPDATE_EFFECT_POSITION,
							data.target,
							targetData
						);
						lastTarget = {
							x: pos?.x,
							y: pos?.y,
							elevation: pos?.elevation,
							width: targetData.width,
							height: targetData.height,
							rotation: targetData.rotation,
							alpha: targetData.alpha,
						};
					}
				}
			});
		}

		if (!data.persist) {
			playData.promise.then(() => this._removeEffect(effect));
		}

		return playData;
	}

	/**
	 * Updates a single effect with the given data
	 *
	 * @param inEffectId
	 * @param inUpdates
	 * @returns {promise|boolean}
	 * @private
	 */
	static _updateEffect(inEffectId, inUpdates) {
		const effect = SequenceManager.VisibleEffects.get(inEffectId);
		if (!effect) return false;
		return effect._update(inUpdates);
	}

	/**
	 * Updates a single effect with new animations
	 *
	 * @param inEffectId
	 * @param inAnimations
	 * @param inLoopingAnimations
	 * @returns {promise|boolean}
	 * @private
	 */
	static _addEffectAnimations(inEffectId, inAnimations, inLoopingAnimations) {
		const effect = SequenceManager.VisibleEffects.get(inEffectId);
		if (!effect) return false;
		return effect._addAnimations(inAnimations, inLoopingAnimations);
	}

	/**
	 * Sets up persisting effects when the scene is first loaded
	 *
	 * @returns {promise}
	 */
	static async initializePersistentEffects() {
		await this.tearDownPersistentEffects();
		let docEffectsMap = {};
		let databaseEffects = foundry.utils.deepClone(flagManager.getDatabaseFlags().effects);
		let effectsToRemove = {}
		for (let [uuid, effects] of Object.entries(databaseEffects)) {
			let doc = fromUuidSync(uuid);
			if (doc instanceof foundry.documents.Actor && doc.prototypeToken.actorLink) {
				for (let token of doc.getActiveTokens()) {
					let tokenEffects = foundry.utils.deepClone(effects);
					docEffectsMap[token.document.uuid] = tokenEffects.map(([id, effect]) => {
						if (lib.is_UUID(effect.source)) {
							effect.source = token.document.uuid;
						}
						effect.sceneId = canvas.scene.id;
						return [id, effect];
					})
				}
			} else if (doc) {
				docEffectsMap[uuid] = effects;
			} else {
				effectsToRemove[uuid] = effects;
			}
		}
		const promises = Object.entries(docEffectsMap)
			.map(([uuid, effects]) => {
				return this._playEffectMap(effects, fromUuidSync(uuid));
			})
			.flat();
		for (let [uuid, effects] of Object.entries(effectsToRemove)) {
			flagManager.removeFlags(uuid, {
				effects, removeAllEffects: true
			});
		}
		return Promise.all(promises).then(() => {
			Hooks.callAll("sequencerEffectManagerReady");
		});
	}

	/**
	 * Tears down persisting effects when the scene is unloaded
	 */
	static tearDownPersistentEffects() {
		return Promise.allSettled(
			this.effects.map((effect) => {
				SequenceManager.VisibleEffects.delete(effect.id);
				return effect.destroy();
			})
		);
	}

	static setup() {
		Hooks.on("createToken", this._documentCreated.bind(this));
		Hooks.on("createDrawing", this._documentCreated.bind(this));
		Hooks.on("createTile", this._documentCreated.bind(this));
		Hooks.on("createMeasuredTemplate", this._documentCreated.bind(this));
		Hooks.on("createRegion", this._documentCreated.bind(this));
	}

	static _patchEffectDataForDocument(inDocumentUuid, effects) {
		return effects.map((effect) => {
			effect[0] = foundry.utils.randomID();
			const effectData = effect[1];
			effectData._id = effect[0];
			if (lib.is_UUID(effectData.source)) {
				if (effectData.masks.includes(effectData.source)) {
					const index = effectData.masks.indexOf(effectData.source);
					effectData.masks[index] = inDocumentUuid;
				}
				effectData.source = inDocumentUuid;
			}
			effectData.sceneId = inDocumentUuid.split(".")[1];
			return effect;
		});
	}

	/**
	 * Plays the effects of a given document on creation
	 *
	 * @param inDocument
	 * @returns {*}
	 */
	static async _documentCreated(inDocument) {
		let effects = flagManager.getEffectFlags(inDocument);
		if (inDocument instanceof TokenDocument && inDocument?.actorLink) {
			let actorEffects = flagManager.getEffectFlags(inDocument?.actor);
			if (actorEffects.length) {
				actorEffects = this._patchEffectDataForDocument(
					inDocument.uuid,
					actorEffects
				);
			}
			effects = effects.concat(actorEffects);
		}
		const sourceUuid = PasteManager.consume(inDocument);
		if (sourceUuid) {
			const sourceEffects = flagManager.getEffectFlags({ uuid: sourceUuid });
			if (sourceEffects.length) {
				const rekeyed = this._patchEffectDataForDocument(inDocument.uuid, sourceEffects);
				const effectDatas = rekeyed.map(([, data]) => data);
				flagManager.addFlags(inDocument.uuid, { effects: effectDatas });
				for (const effectData of effectDatas) {
					sequencerSocket.executeForOthers(SOCKET_HANDLERS.PLAY_EFFECT, effectData, false);
				}
				effects = effects.concat(rekeyed);
			}
		}
		if (!effects?.length) return;
		return this._playEffectMap(effects, inDocument);
	}

	/**
	 * Plays multiple effects at the same time
	 *
	 * @param inEffects
	 * @param inDocument
	 * @returns {Promise<{duration: Promise<number>, promise: Promise<void>}[]>}
	 * @private
	 */
	static _playEffectMap(inEffects, inDocument) {
		if (inEffects instanceof Map) inEffects = Array.from(inEffects);
		return Promise.all(
			inEffects.map((effect) => {
				if (!CanvasEffect.checkShouldPlay(effect[1])) return;
				if (!CanvasEffect.checkValid(effect[1])) {
					if (!game.user.isGM) return;
					lib.custom_warning(
						`Sequencer`,
						`Removed effect from ${inDocument.uuid} as it no longer had a valid source or target`
					);
					return flagManager.removeFlags(inDocument.uuid, { effects: effect });
				}
				return this._playEffect(effect[1], false)
					.then((result) => {
						if (!result) {
							lib.debug("Error playing effect");
						}
					})
					.catch((err) => {
						lib.debug("Error playing effect:", err);
					});
			})
		);
	}

	/**
	 * Ends one or many effects at the same time, returning a promise that resolves once every effect has fully ended
	 *
	 * @param inEffectIds
	 * @returns {Promise}
	 * @private
	 */
	static async _endManyEffects(inEffectIds = false) {
		const actorEffectsToEnd = this.effects.filter((effect) => {
			return (
				effect.context?.actorLink &&
				effect.context?.actor &&
				inEffectIds.includes(effect.data?.persistOptions?.id)
			);
		});
		const effectsByActorUuid = Object.values(
			lib.group_by(actorEffectsToEnd, "context.actor.uuid")
		);

		const regularEffectsToEnd = this.effects.filter((effect) => {
			return (
				inEffectIds.includes(effect.id) ||
				(!(effect.context?.actorLink && effect.context?.actor) &&
					inEffectIds.includes(effect.data?.persistOptions?.id))
			);
		});
		const effectsByContextUuid = Object.values(
			lib.group_by(regularEffectsToEnd, "context.uuid")
		);

		effectsByContextUuid.forEach((effects) => {
			effects = effects.filter(
				(effect) => effect.data.persist && !effect.data.temporary
			);
			if (!effects.length) return;
			const effectData = effects.map((effect) => effect.data);
			flagManager.removeFlags(
				effects[0].context.uuid,
				{ effects: effectData, removeAllEffects: !inEffectIds }
			);
		});

		effectsByActorUuid.forEach((effects) => {
			effects = effects.filter(
				(effect) => effect.data.persist && !effect.data.temporary
			);
			if (!effects.length) return;

			const effectContext = effects[0].context;
			const effectData = effects.map((effect) => effect.data);

			if (
				!(
					effectContext instanceof TokenDocument &&
					effectContext.actorLink &&
					effectContext.actor &&
					effectContext.actor?.prototypeToken?.actorLink
				)
			) {
				return;
			}

			const persistentEffectData = effectData.filter(
				(data) => lib.is_UUID(data?.source) && data?.persistOptions?.persistTokenPrototype
			);
			if (!persistentEffectData.length) return;

			const actorEffects = flagManager.getEffectFlags(effectContext.actor);
			const applicableActorEffects = actorEffects
				.filter((effect) => {
					return (
						lib.is_UUID(effect[1]?.source) &&
						effect[1]?.persistOptions?.persistTokenPrototype &&
						persistentEffectData.some(
							(persistentEffect) =>
								persistentEffect.persistOptions.id ===
								effect[1]?.persistOptions?.id
						)
					);
				})
				.map((e) => e[0]);

			flagManager.removeFlags(
				effectContext.actor.uuid,
				{ effects: applicableActorEffects, removeAllEffects: !inEffectIds }
			);
		});

		const effectsToEnd = effectsByContextUuid
			.concat(effectsByActorUuid)
			.deepFlatten();

		return Promise.allSettled(
			effectsToEnd.map((effect) => this._removeEffect(effect))
		);
	}

	static _effectContextFilter(inUUID, effectData) {
		return (
			effectData?.source === inUUID ||
			effectData?.target === inUUID ||
			(effectData?.tiedDocuments ?? []).indexOf(inUUID) > -1
		);
	}

	/**
	 * Handles the deletion of objects that effects are attached to
	 *
	 * @param inUUID
	 * @returns {Promise}
	 */
	static objectDeleted(inUUID) {
		const databaseEffects = flagManager.getDatabaseFlags().effects;
		const flagRemovals = [];
		for (const [ownerUuid, effects] of Object.entries(databaseEffects)) {
			const idsToEnd = effects
				.filter(([, effectData]) => this._effectContextFilter(inUUID, effectData))
				.map(([effectId]) => effectId);
			if (idsToEnd.length) {
				flagRemovals.push(flagManager.removeFlags(ownerUuid, { effects: idsToEnd }));
			}
		}

		const visibleEffectsToEnd = this.effects
			.filter((effect) => this._effectContextFilter(inUUID, effect.data))
			.map((e) => e.id);

		return Promise.allSettled([
			this._endManyEffects(visibleEffectsToEnd),
			...flagRemovals,
		]);
	}

	/**
	 * Removes the effect from the manager and ends it, returning a promise that resolves once the effect has fully _ended
	 *
	 * @param effect
	 * @returns {Promise}
	 * @private
	 */
	static _removeEffect(effect) {
		SequenceManager.VisibleEffects.delete(effect.id);
		TemporaryPositionsContainer.delete(effect.data.source);
		TemporaryPositionsContainer.delete(effect.data.target);
		return effect.endEffect();
	}
}
